import { create } from "zustand";
import { persist } from "zustand/middleware";
import { HissaReportType, ReportStatus } from "../types/common";

type ReportJob = {
  jobId?: string;
  reportType: HissaReportType;
  customReportId?: string;
  status: ReportStatus["status"];
  progress?: number;
  data?: any;
  error?: string;
  updatedAt: number;
};

// For CUSTOM_REPORT we key by `${reportType}:${customReportId}` so each saved
// custom report tracks its own job state. Other report types continue to key
// purely by reportType, since only one can be in flight per user.
type CompanyReports = Record<string, ReportJob>;

/** Build the per-(company, report) slot key. */
const slotKey = (
  reportType: HissaReportType,
  customReportId?: string
): string =>
  reportType === HissaReportType.CUSTOM_REPORT && customReportId
    ? `${reportType}:${customReportId}`
    : reportType;

type ReportJobStore = {
  reports: Record<string, CompanyReports>;
  upsertReport: (
    companyId: string,
    reportType: HissaReportType,
    payload: Partial<ReportJob>,
    customReportId?: string
  ) => void;
  clearReport: (
    companyId: string,
    reportType: HissaReportType,
    customReportId?: string
  ) => void;
  clearCompany: (companyId: string) => void;
  clearAll: () => void;
};

export const useReportJobStore = create<ReportJobStore>()(
  persist(
    (set) => ({
      reports: {},
      upsertReport: (companyId, reportType, payload, customReportId) =>
        set((state) => {
          const key = slotKey(reportType, customReportId);
          return {
            reports: {
              ...state.reports,
              [companyId]: {
                ...(state.reports[companyId] ?? {}),
                [key]: {
                  ...(state.reports[companyId]?.[key] ?? {}),
                  reportType,
                  ...(customReportId ? { customReportId } : {}),
                  ...payload,
                  updatedAt: payload.updatedAt ?? Date.now(),
                  error: payload.error ?? undefined,
                },
              },
            },
          };
        }),

      clearReport: (companyId, reportType, customReportId) =>
        set((state) => {
          const companyReports = state.reports[companyId];
          if (!companyReports) return state;
          const key = slotKey(reportType, customReportId);
          const { [key]: _, ...rest } = companyReports;
          return {
            reports: {
              ...state.reports,
              [companyId]: rest as CompanyReports,
            },
          };
        }),

      clearCompany: (companyId) =>
        set((state) => {
          const { [companyId]: _, ...rest } = state.reports;
          return { reports: rest };
        }),

      clearAll: () => ({ reports: {} }),
    }),
    {
      name: "job-store",
      partialize: (state) => ({ reports: state.reports }),
    }
  )
);

/** Read a single report job slot, transparently handling CUSTOM_REPORT scoping. */
export function selectReportJob(
  reports: Record<string, Record<string, ReportJob>>,
  companyId: string,
  reportType: HissaReportType,
  customReportId?: string
): ReportJob | undefined {
  return reports[companyId]?.[slotKey(reportType, customReportId)];
}
